import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
} from "@mui/material";
import { Close, Search, DirectionsBoat } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";
import StatusChip from "../common/StatusChip";

// Sailing assignment is per ITEM, not per whole shipment — an order's other
// items may still be in transit to the warehouse or already on a different
// sailing. This dialog lists eligible (received, unassigned) items grouped
// under their parent shipment, so the admin can select individual items —
// or every item on a shipment at once — and assign just those to this
// sailing. Items that belong to a consolidation are still eligible here
// individually (a badge marks them) — consolidation only controls delivery
// grouping, not sailing assignment.
//
// Pass `shipmentId` to scope the list down to a single shipment (used from
// the shipment detail page's "Assign to Sailing" action); omit it to show
// every eligible item across all shipments (used from the Sailing pages).
const AssignShipmentsDialog = ({ open, sailing, onClose, onAssigned, shipmentId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSearch("");
    setLoading(true);
    api
      .get("/sailing/assignable")
      .then(({ data }) => {
        const all = data.data?.items || [];
        setItems(shipmentId ? all.filter((it) => it.shipmentId === shipmentId) : all);
      })
      .catch(() => enqueueSnackbar("Failed to load items", { variant: "error" }))
      .finally(() => setLoading(false));
  }, [open, shipmentId]);

  const filtered = items.filter((it) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      it.shipmentTrackingNumber?.toLowerCase().includes(q) ||
      it.orderNumber?.toLowerCase().includes(q) ||
      it.description?.toLowerCase().includes(q) ||
      it.trackingNumber?.toLowerCase().includes(q) ||
      `${it.customer?.firstName} ${it.customer?.lastName}`.toLowerCase().includes(q)
    );
  });

  // Group flat item list by parent shipment, preserving first-seen order.
  const shipmentGroups = useMemo(() => {
    const groups = new Map();
    for (const it of filtered) {
      if (!groups.has(it.shipmentId)) {
        groups.set(it.shipmentId, {
          shipmentId: it.shipmentId,
          shipmentTrackingNumber: it.shipmentTrackingNumber,
          orderNumber: it.orderNumber,
          customer: it.customer,
          items: [],
        });
      }
      groups.get(it.shipmentId).items.push(it);
    }
    return [...groups.values()];
  }, [filtered]);

  const toggleItem = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleShipment = (group) => {
    const allIn = group.items.every((it) => selected.has(it._id));
    setSelected((prev) => {
      const next = new Set(prev);
      group.items.forEach((it) => (allIn ? next.delete(it._id) : next.add(it._id)));
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((it) => it._id)));
    }
  };

  const handleAssign = async () => {
    if (selected.size === 0) return;
    setAssigning(true);
    try {
      const itemById = new Map(items.map((it) => [it._id, it]));
      const itemRefs = [...selected].map((id) => ({
        shipmentId: itemById.get(id).shipmentId,
        itemId: id,
      }));
      const { data } = await api.post(`/sailing/${sailing._id}/assign`, { items: itemRefs });
      enqueueSnackbar(`${selected.size} item(s) assigned to ${sailing.sailingNumber}`, {
        variant: "success",
      });
      onAssigned(data.data?.schedule);
      onClose();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to assign items", { variant: "error" });
    } finally {
      setAssigning(false);
    }
  };

  if (!sailing) return null;

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const someSelected = selected.size > 0 && selected.size < filtered.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                bgcolor: "#001A3C",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DirectionsBoat sx={{ color: "white", fontSize: 18 }} />
            </Box>
            <Typography fontWeight={700} fontSize={16}>
              Assign Items to Sailing
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", ml: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>{sailing.sailingNumber}</strong> · {sailing.vesselName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ·
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Departs {dayjs(sailing.departureDate).format("MMM D, YYYY")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ·
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sailing.originPort} → {sailing.destinationPort}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: 0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Current assignment count — translucent primary.main tint instead
            of a solid light-blue hex, so it blends into the dialog's own
            background in dark mode instead of staying a bright pastel
            banner (text color here is already "primary.main", which does
            adapt per mode on its own). */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: "rgba(13,98,155,0.08)",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2" color="primary.main" fontWeight={600}>
            Currently assigned: {sailing.itemCount ?? sailing.items?.length ?? 0} item(s)
          </Typography>
          {selected.size > 0 && (
            <Chip
              label={`${selected.size} selected`}
              size="small"
              sx={{
                bgcolor: "primary.main",
                color: "white",
                fontWeight: 600,
                fontSize: 11,
              }}
            />
          )}
        </Box>

        {/* Search */}
        {!shipmentId && (
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search by tracking #, order #, item, or customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}

        {loading && <LinearProgress />}

        {!loading && items.length === 0 && (
          <Alert severity="info" sx={{ m: 2.5, borderRadius: 2 }}>
            No items are currently ready to be assigned. Items must be in{" "}
            <strong>Item Received</strong> status and not already on a sailing.
          </Alert>
        )}

        {!loading && items.length > 0 && filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
            No items match your search.
          </Typography>
        )}

        {!loading && filtered.length > 0 && (
          <Box sx={{ overflowX: "auto", maxHeight: 440, overflowY: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: "action.hover" }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell sx={{ bgcolor: "action.hover" }}>Item</TableCell>
                  <TableCell sx={{ bgcolor: "action.hover" }}>Tracking #</TableCell>
                  <TableCell sx={{ bgcolor: "action.hover" }}>D/R</TableCell>
                  <TableCell sx={{ bgcolor: "action.hover" }} align="right">
                    Weight / CBF
                  </TableCell>
                  <TableCell sx={{ bgcolor: "action.hover" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipmentGroups.map((group) => {
                  const groupAllSelected = group.items.every((it) => selected.has(it._id));
                  const groupSomeSelected =
                    !groupAllSelected && group.items.some((it) => selected.has(it._id));
                  return (
                    <React.Fragment key={group.shipmentId}>
                      {/* Shipment header row — dark/bold so it visually
                          stands apart from the item rows nested under it */}
                      <TableRow
                        hover
                        onClick={() => toggleShipment(group)}
                        sx={{
                          cursor: "pointer",
                          bgcolor: "#001A3C",
                          "&.MuiTableRow-hover:hover": { bgcolor: "#012452" },
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ borderBottom: "none" }}>
                          <Checkbox
                            size="small"
                            checked={groupAllSelected}
                            indeterminate={groupSomeSelected}
                            onChange={() => toggleShipment(group)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: "rgba(255,255,255,0.7)",
                              "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "white" },
                            }}
                          />
                        </TableCell>
                        <TableCell colSpan={4} sx={{ borderBottom: "none" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                            <Typography
                              variant="body2"
                              fontWeight={800}
                              sx={{ fontFamily: "monospace", color: "white" }}
                            >
                              {group.shipmentTrackingNumber}
                            </Typography>
                            {group.orderNumber && (
                              <Typography variant="caption" fontWeight={700} sx={{ color: "rgba(255,255,255,0.75)" }}>
                                Order {group.orderNumber}
                              </Typography>
                            )}
                            <Typography variant="caption" fontWeight={700} sx={{ color: "rgba(255,255,255,0.75)" }}>
                              {group.customer?.firstName} {group.customer?.lastName}
                            </Typography>
                            <Chip
                              label={`${group.items.length} item(s)`}
                              size="small"
                              sx={{
                                bgcolor: "rgba(255,255,255,0.15)",
                                color: "white",
                                fontWeight: 700,
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Item rows, nested under the shipment */}
                      {group.items.map((it) => {
                        const isSelected = selected.has(it._id);
                        return (
                          <TableRow
                            key={it._id}
                            hover
                            onClick={() => toggleItem(it._id)}
                            sx={{
                              cursor: "pointer",
                              bgcolor: isSelected ? "rgba(13,98,155,0.04)" : "transparent",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => toggleItem(it._id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell sx={{ pl: 4 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Typography variant="body2" noWrap maxWidth={220}>
                                  {it.description || "—"}
                                </Typography>
                                {it.consolidation && (
                                  <Chip
                                    label={`Consol. ${it.consolidation.consolidationNumber || ""}`}
                                    size="small"
                                    sx={{
                                      bgcolor: "warning.light",
                                      color: "warning.main",
                                      fontWeight: 600,
                                      fontSize: 10,
                                      height: 18,
                                      "& .MuiChip-label": { px: 0.75 },
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}
                            >
                              {it.trackingNumber || "—"}
                            </TableCell>
                            <TableCell
                              sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}
                            >
                              {it.dockReceiptNumber || "—"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, color: "text.secondary" }}>
                              {it.weight != null ? `${it.weight} lb` : "—"} /{" "}
                              {it.cbf != null ? `${it.cbf} cbf` : "—"}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={it.itemStatus} domain="item" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {filtered.length} item(s) available · {selected.size} selected
        </Typography>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={selected.size === 0 || assigning}
          startIcon={assigning ? null : <DirectionsBoat />}
        >
          {assigning ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            `Assign ${selected.size > 0 ? selected.size : ""} Item${selected.size !== 1 ? "s" : ""}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignShipmentsDialog;
